import { Component } from '@theme/component';

/**
 * @typedef {Object} ReadMoreRefs
 * @property {HTMLElement} readMoreContent - The text content that gets clamped/expanded
 * @property {HTMLElement} readMoreToggle - The button that toggles the expanded state
 */

/**
 * A custom element that clamps text to 2 lines and reveals a toggle button
 * to expand/collapse the rest of the content when it overflows.
 *
 * @extends {Component<ReadMoreRefs>}
 */
class ReadMoreComponent extends Component {
  requiredRefs = ['readMoreContent', 'readMoreToggle'];

  /**
   * @type {ResizeObserver | undefined}
   */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#updateToggleVisibility();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#updateToggleVisibility);
      this.#resizeObserver.observe(this.refs.readMoreContent);
    } else {
      window.addEventListener('resize', this.#updateToggleVisibility);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    window.removeEventListener('resize', this.#updateToggleVisibility);
  }

  /**
   * Shows the toggle button only when the content is actually clamped/overflowing.
   */
  #updateToggleVisibility = () => {
    if (this.dataset.expanded === 'true') return;

    const { readMoreContent, readMoreToggle } = this.refs;
    const isOverflowing = readMoreContent.scrollHeight - readMoreContent.clientHeight > 1;

    readMoreToggle.hidden = !isOverflowing;
  };

  /**
   * Toggles between the clamped and expanded states.
   *
   * @param {Event} event - The click event
   */
  toggle = (event) => {
    event.preventDefault();

    const expanded = this.dataset.expanded === 'true';

    this.dataset.expanded = expanded ? 'false' : 'true';
    this.refs.readMoreToggle.setAttribute('aria-expanded', String(!expanded));
  };
}

if (!customElements.get('read-more-component')) {
  customElements.define('read-more-component', ReadMoreComponent);
}
